import { httpsCallable } from 'firebase/functions'

import { functions } from '../../../firebase'

export interface MediaManagementStatus {
  totalGifs: number
  gifsWithMp4: number
  gifsMissingMp4: number
  gifsWithLoopIssues: number
  missingMp4List: {
    gifId: string
    fileName: string
    fileSizeBytes: number
    uploadedAt: string
  }[]
  loopIssuesList: {
    gifId: string
    fileName: string
    fileSizeBytes: number
    uploadedAt: string
    loopInfo: {
      hasLoopExtension: boolean
      isGif89a: boolean
      frameCount: number
      needsRepair: boolean
    }
  }[]
  hasMore?: boolean
  nextOffset?: number
}

export interface BatchConversionResult {
  totalRequested: number
  successful: number
  failed: number
  results: {
    gifId: string
    success: boolean
    error?: string
    mp4SizeBytes?: number
    processingTimeMs?: number
  }[]
}

export interface GifCounterpartStatusResult {
  totalMp4s: number
  mp4sWithGif: number
  mp4sMissingGif: number
  missingGifList: {
    gifId: string
    fileName: string
    fileSizeBytes: number
    uploadedAt: string
  }[]
  hasMore?: boolean
  nextOffset?: number
}

export interface GifDocumentsStatusResult {
  totalGifDocs: number
  docsWithGifFile: number
  docsMissingGifFile: number
  missingGifList: {
    gifId: string
    fileName: string
    fileSizeBytes: number
    uploadedAt: string
    canRepair: boolean
  }[]
  hasMore?: boolean
  nextOffset?: number
}

export interface BatchLoopRepairResult {
  totalRequested: number
  successful: number
  failed: number
  results: {
    gifId: string
    success: boolean
    error?: string
    originalSizeBytes?: number
    repairedSizeBytes?: number
    processingTimeMs?: number
  }[]
}

/**
 * Get media management status - finds GIFs missing MP4 counterparts (with pagination support)
 */
export const getMediaManagementStatus = async (params?: {
  offset?: number
  limit?: number
}): Promise<MediaManagementStatus> => {
  try {
    // Validate parameters client-side
    if (params?.offset !== undefined && params.offset < 0) {
      throw new Error('Offset must be >= 0')
    }
    if (
      params?.limit !== undefined &&
      (params.limit < 1 || params.limit > 100)
    ) {
      throw new Error('Limit must be between 1 and 100')
    }

    const getMediaManagementStatusFunction = httpsCallable<
      { offset?: number; limit?: number },
      { success: boolean; data?: MediaManagementStatus; error?: string }
    >(functions, 'getMediaManagementStatus')

    const result = await getMediaManagementStatusFunction(params || {})

    if (!result.data.success) {
      throw new Error(
        result.data.error || 'Failed to get media management status'
      )
    }

    return result.data.data!
  } catch (error) {
    console.error('Error getting media management status:', error)
    throw new Error(
      `Failed to get media management status: ${
        error instanceof Error ? error.message : 'Unknown error'
      }`
    )
  }
}

/**
 * Convert selected GIFs to MP4 format in batch
 */
export const batchConvertGifsToMp4 = async (
  gifIds: string[]
): Promise<BatchConversionResult> => {
  try {
    if (!gifIds || gifIds.length === 0) {
      throw new Error('At least one GIF ID is required')
    }

    if (gifIds.length > 50) {
      throw new Error('Maximum 50 GIFs can be converted in one batch')
    }

    const batchConvertFunction = httpsCallable<
      { gifIds: string[] },
      { success: boolean; data?: BatchConversionResult; error?: string }
    >(functions, 'batchConvertGifsToMp4')

    const result = await batchConvertFunction({ gifIds })

    if (!result.data.success) {
      throw new Error(result.data.error || 'Failed to convert GIFs to MP4')
    }

    return result.data.data!
  } catch (error) {
    console.error('Error converting GIFs to MP4:', error)
    throw new Error(
      `Failed to convert GIFs to MP4: ${
        error instanceof Error ? error.message : 'Unknown error'
      }`
    )
  }
}

/**
 * Get MP4 counterpart status - finds MP4s missing GIF counterparts (with pagination)
 */
export const getGifCounterpartStatus = async (params?: {
  offset?: number
  limit?: number
}): Promise<GifCounterpartStatusResult> => {
  try {
    if (params?.offset !== undefined && params.offset < 0) {
      throw new Error('Offset must be >= 0')
    }
    if (
      params?.limit !== undefined &&
      (params.limit < 1 || params.limit > 100)
    ) {
      throw new Error('Limit must be between 1 and 100')
    }

    const fn = httpsCallable<
      { offset?: number; limit?: number },
      { success: boolean; data?: GifCounterpartStatusResult; error?: string }
    >(functions, 'getGifCounterpartStatus')

    const result = await fn(params || {})
    if (!result.data.success) {
      throw new Error(
        result.data.error || 'Failed to get GIF counterpart status'
      )
    }
    return result.data.data!
  } catch (error) {
    console.error('Error getting GIF counterpart status:', error)
    throw new Error(
      `Failed to get GIF counterpart status: ${
        error instanceof Error ? error.message : 'Unknown error'
      }`
    )
  }
}

/**
 * Scan Firestore gif documents and verify storage file existence
 */
export const getGifDocumentsStatus = async (params?: {
  offset?: number
  limit?: number
}): Promise<GifDocumentsStatusResult> => {
  try {
    if (params?.offset !== undefined && params.offset < 0) {
      throw new Error('Offset must be >= 0')
    }
    if (
      params?.limit !== undefined &&
      (params.limit < 1 || params.limit > 500)
    ) {
      throw new Error('Limit must be between 1 and 500')
    }

    const fn = httpsCallable<
      { offset?: number; limit?: number },
      { success: boolean; data?: GifDocumentsStatusResult; error?: string }
    >(functions, 'getGifDocumentsStatus')

    const result = await fn(params || {})
    if (!result.data.success) {
      throw new Error(result.data.error || 'Failed to get GIF documents status')
    }
    return result.data.data!
  } catch (error) {
    console.error('Error getting GIF documents status:', error)
    throw new Error(
      `Failed to get GIF documents status: ${
        error instanceof Error ? error.message : 'Unknown error'
      }`
    )
  }
}

/**
 * Convert selected MP4s to GIF format in batch
 */
export const batchConvertMp4sToGif = async (
  gifIds: string[]
): Promise<BatchConversionResult> => {
  try {
    if (!gifIds || gifIds.length === 0) {
      throw new Error('At least one GIF ID is required')
    }
    if (gifIds.length > 50) {
      throw new Error('Maximum 50 MP4s can be converted in one batch')
    }

    const fn = httpsCallable<
      { gifIds: string[] },
      { success: boolean; data?: BatchConversionResult; error?: string }
    >(functions, 'batchConvertMp4sToGif')

    const result = await fn({ gifIds })
    if (!result.data.success) {
      throw new Error(result.data.error || 'Failed to convert MP4s to GIF')
    }
    return result.data.data!
  } catch (error) {
    console.error('Error converting MP4s to GIF:', error)
    throw new Error(
      `Failed to convert MP4s to GIF: ${
        error instanceof Error ? error.message : 'Unknown error'
      }`
    )
  }
}

/**
 * Format file size for display
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B'

  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`
}

/**
 * Format processing time for display
 */
export const formatProcessingTime = (ms: number): string => {
  if (ms < 1000) {
    return `${ms}ms`
  } else if (ms < 60000) {
    return `${(ms / 1000).toFixed(1)}s`
  } else {
    return `${(ms / 60000).toFixed(1)}m`
  }
}

/**
 * Get detailed loop analysis for GIFs with issues (with pagination support)
 */
export const getGifLoopDetails = async (params?: {
  gifIds?: string[]
  offset?: number
  limit?: number
}): Promise<{
  loopIssuesList: {
    gifId: string
    fileName: string
    fileSizeBytes: number
    uploadedAt: string
    loopInfo: {
      hasLoopExtension: boolean
      isGif89a: boolean
      frameCount: number
      needsRepair: boolean
    }
  }[]
  totalAnalyzed: number
  hasMore: boolean
  nextOffset: number
}> => {
  try {
    // Validate parameters client-side
    if (params?.offset !== undefined && params.offset < 0) {
      throw new Error('Offset must be >= 0')
    }
    if (
      params?.limit !== undefined &&
      (params.limit < 1 || params.limit > 200)
    ) {
      throw new Error('Limit must be between 1 and 200')
    }
    if (params?.gifIds && params.gifIds.length > 100) {
      throw new Error('Maximum 100 GIF IDs can be analyzed at once')
    }

    const getLoopDetailsFunction = httpsCallable<
      { gifIds?: string[]; offset?: number; limit?: number },
      {
        success: boolean
        data?: {
          loopIssuesList: Array<{
            gifId: string
            fileName: string
            fileSizeBytes: number
            uploadedAt: string
            loopInfo: {
              hasLoopExtension: boolean
              isGif89a: boolean
              frameCount: number
              needsRepair: boolean
            }
          }>
          totalAnalyzed: number
          hasMore: boolean
          nextOffset: number
        }
        error?: string
      }
    >(functions, 'getGifLoopDetails')

    const result = await getLoopDetailsFunction(params || {})

    if (!result.data.success) {
      throw new Error(result.data.error || 'Failed to get loop details')
    }

    return result.data.data!
  } catch (error) {
    console.error('Error getting loop details:', error)
    throw new Error(
      `Failed to get loop details: ${
        error instanceof Error ? error.message : 'Unknown error'
      }`
    )
  }
}

/**
 * Repair selected GIFs by adding proper loop extensions
 */
export const batchRepairGifLoops = async (
  gifIds: string[]
): Promise<BatchLoopRepairResult> => {
  try {
    if (!gifIds || gifIds.length === 0) {
      throw new Error('At least one GIF ID is required')
    }

    if (gifIds.length > 50) {
      throw new Error('Maximum 50 GIFs can be repaired in one batch')
    }

    const batchRepairFunction = httpsCallable<
      { gifIds: string[] },
      { success: boolean; data?: BatchLoopRepairResult; error?: string }
    >(functions, 'batchRepairGifLoops')

    const result = await batchRepairFunction({ gifIds })

    if (!result.data.success) {
      throw new Error(result.data.error || 'Failed to repair GIF loops')
    }

    return result.data.data!
  } catch (err) {
    console.error('Error repairing GIF loops:', err)
    throw new Error(
      `Failed to repair GIF loops: ${
        err instanceof Error ? err.message : 'Unknown error'
      }`
    )
  }
}

/**
 * Format upload date for display
 */
export const formatUploadDate = (dateString: string): string => {
  if (dateString === 'Unknown') return 'Unknown'

  try {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return 'Invalid date'
  }
}

/**
 * Interface for GIF URL format analysis results
 */
export interface GifUrlAnalysisResult {
  totalGifs: number
  urlFormats: {
    storagePathFormat: {
      count: number
      pattern: string
      examples: string[]
    }
    fullUrlFormat: {
      count: number
      pattern: string
      examples: string[]
    }
    otherFormats: {
      count: number
      examples: { url: string; gifId: string }[]
    }
  }
  inconsistentGifs: {
    gifId: string
    gifUrl: string
    format: 'storage-path' | 'full-url' | 'other'
  }[]
  recommendations: string[]
}

/**
 * Analyze all GIF URLs in the database to identify format inconsistencies
 */
export const analyzeGifUrlFormats = async (): Promise<GifUrlAnalysisResult> => {
  try {
    const analyzeFunction = httpsCallable<
      Record<string, never>,
      { success: boolean; data?: GifUrlAnalysisResult; error?: string }
    >(functions, 'analyzeGifUrlFormats')

    const result = await analyzeFunction({})

    if (!result.data.success) {
      throw new Error(result.data.error || 'Failed to analyze GIF URL formats')
    }

    return result.data.data!
  } catch (err) {
    console.error('Error analyzing GIF URL formats:', err)
    throw new Error(
      `Failed to analyze GIF URL formats: ${
        err instanceof Error ? err.message : 'Unknown error'
      }`
    )
  }
}

/**
 * Interface for GIF URL repair results
 */
export interface GifUrlRepairResult {
  dryRun: boolean
  totalProcessed: number
  repairsNeeded: number
  repairsCompleted: number
  repairsFailed: number
  results: {
    gifId: string
    originalUrl: string
    repairedUrl: string
    status: 'success' | 'failed' | 'skipped'
    error?: string
  }[]
  summary: {
    beforeRepair: {
      storagePathFormat: number
      fullUrlFormat: number
      otherFormats: number
    }
    afterRepair: {
      storagePathFormat: number
      fullUrlFormat: number
      otherFormats: number
    }
  }
}

/**
 * Repair GIF URLs in the database by converting full URLs to storage path format
 */
export const repairGifUrlFormats = async (options: {
  dryRun?: boolean
  maxRepairs?: number
  specificGifIds?: string[]
}): Promise<GifUrlRepairResult> => {
  try {
    const repairFunction = httpsCallable<
      {
        dryRun?: boolean
        maxRepairs?: number
        specificGifIds?: string[]
      },
      { success: boolean; data?: GifUrlRepairResult; error?: string }
    >(functions, 'repairGifUrlFormats')

    const result = await repairFunction(options)

    if (!result.data.success) {
      throw new Error(result.data.error || 'Failed to repair GIF URL formats')
    }

    return result.data.data!
  } catch (err) {
    console.error('Error repairing GIF URL formats:', err)
    throw new Error(
      `Failed to repair GIF URL formats: ${
        err instanceof Error ? err.message : 'Unknown error'
      }`
    )
  }
}

/**
 * Interface for file size migration results
 */
export interface FileSizeMigrationResult {
  dryRun: boolean
  totalDocuments: number
  processed: number
  updated: number
  skipped: number
  failed: number
  results: {
    gifId: string
    status: 'updated' | 'skipped' | 'failed'
    fileSizeBytes?: number
    error?: string
  }[]
}

/**
 * Migrate GIF documents by adding fileSizeBytes from Storage metadata
 */
export const migrateGifFileSizes = async (options: {
  dryRun?: boolean
  batchSize?: number
  forceUpdate?: boolean
}): Promise<FileSizeMigrationResult> => {
  try {
    const migrateFunction = httpsCallable<
      {
        dryRun?: boolean
        batchSize?: number
        forceUpdate?: boolean
      },
      { success: boolean; data?: FileSizeMigrationResult; error?: string }
    >(functions, 'migrateGifFileSizes')

    const result = await migrateFunction(options)

    if (!result.data.success) {
      throw new Error(result.data.error || 'Failed to migrate file sizes')
    }

    return result.data.data!
  } catch (err) {
    console.error('Error migrating file sizes:', err)
    throw new Error(
      `Failed to migrate file sizes: ${
        err instanceof Error ? err.message : 'Unknown error'
      }`
    )
  }
}

/**
 * Interface for video metadata analysis results
 */
export interface VideoMetadataAnalysisResult {
  totalMp4Files: number
  filesWithMetadata: number
  filesMissingMetadata: number
  metadataStatus: {
    complete: {
      count: number
      examples: {
        gifId: string
        fileName: string
        dimensions: { width: number; height: number }
        addedAt?: string
      }[]
    }
    missing: {
      count: number
      examples: {
        gifId: string
        fileName: string
        fileSizeBytes: number
      }[]
    }
  }
  recommendations: string[]
}

/**
 * Interface for video metadata repair results
 */
export interface VideoMetadataRepairResult {
  dryRun: boolean
  totalProcessed: number
  repairsNeeded: number
  repairsCompleted: number
  repairsFailed: number
  results: {
    gifId: string
    fileName: string
    status: 'success' | 'failed' | 'skipped'
    error?: string
    dimensions?: { width: number; height: number }
    processingTimeMs?: number
  }[]
}

export interface MediaOverviewStats {
  totalGifDocuments: number
  totalGifFiles: number
  totalMp4Files: number
}

/**
 * Get high-level media overview statistics for sanity checking
 */
export const getMediaOverviewStats = async (): Promise<MediaOverviewStats> => {
  try {
    const getStatsFunction = httpsCallable<
      Record<string, never>,
      { success: boolean; data?: MediaOverviewStats; error?: string }
    >(functions, 'getMediaOverviewStats')

    const result = await getStatsFunction({})

    if (!result.data.success) {
      throw new Error(result.data.error || 'Failed to get media overview stats')
    }

    return result.data.data!
  } catch (err) {
    console.error('Error getting media overview stats:', err)
    throw new Error(
      `Failed to get media overview stats: ${
        err instanceof Error ? err.message : 'Unknown error'
      }`
    )
  }
}

/**
 * Analyze all MP4 files in Firebase Storage to identify which ones are missing video dimension metadata
 */
export const analyzeVideoMetadata =
  async (): Promise<VideoMetadataAnalysisResult> => {
    try {
      const analyzeFunction = httpsCallable<
        Record<string, never>,
        { success: boolean; data?: VideoMetadataAnalysisResult; error?: string }
      >(functions, 'analyzeVideoMetadata')

      const result = await analyzeFunction({})

      if (!result.data.success) {
        throw new Error(result.data.error || 'Failed to analyze video metadata')
      }

      return result.data.data!
    } catch (err) {
      console.error('Error analyzing video metadata:', err)
      throw new Error(
        `Failed to analyze video metadata: ${
          err instanceof Error ? err.message : 'Unknown error'
        }`
      )
    }
  }

/**
 * Repair MP4 files by adding video dimensions to their Firebase Storage metadata
 */
export const repairVideoMetadata = async (options: {
  dryRun?: boolean
  maxRepairs?: number
  specificGifIds?: string[]
}): Promise<VideoMetadataRepairResult> => {
  try {
    // Validate maxRepairs limit client-side
    if (
      options.maxRepairs !== undefined &&
      (options.maxRepairs < 1 || options.maxRepairs > 500)
    ) {
      throw new Error('Max repairs must be between 1 and 500')
    }

    const repairFunction = httpsCallable<
      {
        dryRun?: boolean
        maxRepairs?: number
        specificGifIds?: string[]
      },
      { success: boolean; data?: VideoMetadataRepairResult; error?: string }
    >(functions, 'repairVideoMetadata')

    const result = await repairFunction(options)

    if (!result.data.success) {
      throw new Error(result.data.error || 'Failed to repair video metadata')
    }

    return result.data.data!
  } catch (err) {
    console.error('Error repairing video metadata:', err)
    throw new Error(
      `Failed to repair video metadata: ${
        err instanceof Error ? err.message : 'Unknown error'
      }`
    )
  }
}

/**
 * Interface for comprehensive media matching results
 */
export interface ComprehensiveMediaMatchingResult {
  entries: {
    gifId: string
    documentExists: boolean
    gifFileExists: boolean
    mp4FileExists: boolean
    gifFileName?: string
    mp4FileName?: string
    gifFileSize?: number
    mp4FileSize?: number
    gifUploadedAt?: string
    mp4UploadedAt?: string
    documentGifUrl?: string
    issues: string[]
  }[]
  summary: {
    totalEntries: number
    completeMatches: number
    missingDocuments: number
    missingGifFiles: number
    missingMp4Files: number
  }
}

/**
 * Get comprehensive media matching data showing how documents, GIF files, and MP4 files match up
 */
export const getComprehensiveMediaMatching =
  async (): Promise<ComprehensiveMediaMatchingResult> => {
    try {
      const getMatchingFunction = httpsCallable<
        Record<string, never>,
        {
          success: boolean
          data?: ComprehensiveMediaMatchingResult
          error?: string
        }
      >(functions, 'getComprehensiveMediaMatching')

      const result = await getMatchingFunction({})

      if (!result.data.success) {
        throw new Error(
          result.data.error || 'Failed to get comprehensive media matching'
        )
      }

      return result.data.data!
    } catch (err) {
      console.error('Error getting comprehensive media matching:', err)
      throw new Error(
        `Failed to get comprehensive media matching: ${
          err instanceof Error ? err.message : 'Unknown error'
        }`
      )
    }
  }

export const reencodeGifMp4 = async (options: {
  gifId: string
  source?: 'gif' | 'mp4'
}): Promise<{
  gifId: string
  width: number
  height: number
  fileSizeBytes: number
  durationSeconds: number
  processingTimeMs: number
  sourceUsed: string
}> => {
  try {
    if (!options.gifId) throw new Error('gifId is required')

    const fn = httpsCallable<
      { gifId: string; source?: 'gif' | 'mp4' },
      {
        success: boolean
        data?: {
          gifId: string
          width: number
          height: number
          fileSizeBytes: number
          durationSeconds: number
          processingTimeMs: number
          sourceUsed: string
        }
        error?: string
      }
    >(functions, 'reencodeGifMp4')

    const result = await fn(options)
    if (!result.data.success) {
      throw new Error(result.data.error || 'Failed to re-encode MP4')
    }
    return result.data.data!
  } catch (err) {
    console.error('Error re-encoding MP4:', err)
    throw new Error(
      `Failed to re-encode MP4: ${err instanceof Error ? err.message : 'Unknown error'}`
    )
  }
}
